import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    OndemandVideoOutlined, ChatBubbleOutlineOutlined,
    EventNoteOutlined, SettingsOutlined,
} from '@mui/icons-material';
import { PRIMARY, CANVAS_W, CANVAS_H, ASPECT } from '../constants';

const PLENARY_COLOR = '#8b4fcc';

/**
 * A schematic preview of the plenary page layout rendered at canvas scale.
 * No drag-and-drop. Clicking a zone selects it and shows the correct inspector.
 *
 * Props:
 *   plenary          — PlenaryPage document
 *   selectedComponent — 'page' | 'video' | 'chat' | 'session' | null
 *   onSelectComponent — (id: string) => void
 *   previewMode       — boolean
 */
export function PlenaryCanvas({ plenary, selectedComponent, onSelectComponent, previewMode }) {
    if (!plenary) return null;

    const bg = plenary.bgType === 'color' || !plenary.bgType
        ? { bgcolor: plenary.bgColor || '#0D1B2A' }
        : plenary.bgType === 'image' && plenary.bgUrl
            ? { backgroundImage: `url(${plenary.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { bgcolor: '#0D1B2A' };

    return (
        <Box
            sx={{
                flex: 1,
                overflow: 'auto',
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: `calc((100vh - 60px - 48px - 120px - 48px) / ${ASPECT})`,
                    height: '100%',
                    maxHeight: 650,
                }}
            >
                {/* Ratio box — also the positioning context for the toolbar */}
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>


                    <Box
                        onClick={() => onSelectComponent('page')}
                        sx={{
                            position: 'relative', width: '100%', height: '100%',
                            display: 'flex', alignItems: 'stretch', justifyContent: 'center',
                            cursor: 'default', overflow: 'hidden',
                            ...bg,
                        }}
                    >
                        {/* Dark overlay (mirrors the real page) */}
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.55)', zIndex: 0 }} />

                        {/* Page-selected ring */}
                        {selectedComponent === 'page' && !previewMode && (
                            <Box sx={{
                                position: 'absolute', inset: 0, zIndex: 10,
                                border: `2px solid ${PLENARY_COLOR}`,
                                pointerEvents: 'none', borderRadius: 0,
                            }} />
                        )}

                        {/* ── Layout: left column (video + session) + right column (chat) ── */}
                        <Box sx={{
                            position: 'relative', zIndex: 1,
                            display: 'flex', width: '100%', height: '100%',
                            p: '20px', gap: '8px', maxWidth: 800,
                        }}>

                            {/* Left: video + session info */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>

                                {/* ── Video Player zone ── */}
                                <Zone
                                    id="video"
                                    selected={selectedComponent === 'video'}
                                    onSelect={onSelectComponent}
                                    previewMode={previewMode}
                                    accentColor="#EF4444"
                                    sx={{ flex: '0 0 auto', aspectRatio: '16/9', width: '100%' }}
                                >
                                    <VideoZoneContent plenary={plenary} />
                                </Zone>

                                {/* ── Session Info zone ── */}
                                <Zone
                                    id="session"
                                    selected={selectedComponent === 'session'}
                                    onSelect={onSelectComponent}
                                    previewMode={previewMode}
                                    accentColor="#1075BA"
                                    sx={{ flex: 1 }}
                                >
                                    <SessionZoneContent plenary={plenary} />
                                </Zone>
                            </Box>

                            {/* Right: chat (hidden if disabled) */}
                            {plenary.chatEnabled !== false && (
                                <Zone
                                    id="chat"
                                    selected={selectedComponent === 'chat'}
                                    onSelect={onSelectComponent}
                                    previewMode={previewMode}
                                    accentColor="#22c55e"
                                    sx={{ width: '22%', flexShrink: 0, minWidth: 64 }}
                                >
                                    <ChatZoneContent plenary={plenary} />
                                </Zone>
                            )}

                            {/* Chat disabled placeholder */}
                            {plenary.chatEnabled === false && !previewMode && (
                                <Box sx={{
                                    width: '22%', flexShrink: 0, minWidth: 64,
                                    border: '1px dashed rgba(255,255,255,0.1)',
                                    borderRadius: '6px',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    gap: 0.5, opacity: 0.35,
                                }}>
                                    <ChatBubbleOutlineOutlined sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                                    <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                        Chat disabled
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Page label (top-left corner) */}
                        {!previewMode && (
                            <Box sx={{
                                position: 'absolute', top: 6, left: 6, zIndex: 20,
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                background: alpha(PLENARY_COLOR, 0.9), color: '#fff',
                                fontSize: '0.55rem', fontWeight: 700, px: 0.75, py: 0.3,
                                borderRadius: '4px', letterSpacing: '0.06em',
                                pointerEvents: 'none',
                            }}>
                                <OndemandVideoOutlined sx={{ fontSize: 9 }} />
                                PLENARY · {plenary.name}
                            </Box>
                        )}

                        {/* Click-canvas-for-settings hint */}
                        {!previewMode && !selectedComponent && (
                            <Box sx={{
                                position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                                zIndex: 20, display: 'flex', alignItems: 'center', gap: 0.75,
                                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
                                color: 'rgba(255,255,255,0.7)', fontSize: '0.62rem', px: 1.5, py: 0.6,
                                borderRadius: '20px', pointerEvents: 'none', whiteSpace: 'nowrap',
                            }}>
                                <SettingsOutlined sx={{ fontSize: 11 }} />
                                Click a component to edit its settings
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>

    );
}

// ── Reusable clickable zone wrapper ──────────────────────────────────────────
function Zone({ id, selected, onSelect, previewMode, accentColor, sx = {}, children }) {
    return (
        <Box
            onClick={e => { e.stopPropagation(); if (!previewMode) onSelect(id); }}
            sx={{
                position: 'relative', borderRadius: '6px', overflow: 'hidden',
                cursor: previewMode ? 'default' : 'pointer',
                transition: 'box-shadow 0.15s, outline 0.15s',
                outline: selected && !previewMode ? `2px solid ${accentColor}` : '2px solid transparent',
                outlineOffset: selected ? '-2px' : '-2px',
                boxShadow: selected && !previewMode ? `0 0 0 4px ${alpha(accentColor, 0.2)}` : 'none',
                '&:hover': !previewMode ? { outline: `2px solid ${alpha(accentColor, 0.5)}` } : {},
                ...sx,
            }}
        >
            {children}

            {/* Zone label badge on hover / selection */}
            {!previewMode && (
                <Box sx={{
                    position: 'absolute', top: 4, right: 4, zIndex: 10,
                    background: selected ? accentColor : 'rgba(0,0,0,0.55)',
                    color: '#fff', fontSize: '0.5rem', fontWeight: 700,
                    px: 0.6, py: 0.2, borderRadius: '3px', letterSpacing: '0.05em',
                    opacity: selected ? 1 : 0,
                    transition: 'opacity 0.12s',
                    '.zone-wrap:hover &': { opacity: 1 },
                    pointerEvents: 'none',
                }}>
                    {ZONE_LABELS[id]}
                </Box>
            )}
        </Box>
    );
}

const ZONE_LABELS = {
    video: 'VIDEO PLAYER',
    chat: 'CHAT',
    session: 'SESSION INFO',
};

// ── Video zone content ────────────────────────────────────────────────────────
function VideoZoneContent({ plenary }) {
    const hasStream = !!plenary.streamUrl;

    return (
        <Box sx={{
            width: '100%', height: '100%', bgcolor: '#000',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 0.75, position: 'relative',
            minHeight: 60,
        }}>
            {hasStream ? (
                <>
                    {/* Simulated video frame */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1030 50%, #0a0a1a 100%)',
                    }} />
                    {/* Scanline texture */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.015) 2px, rgba(255,255,255,0.015) 4px)',
                        pointerEvents: 'none',
                    }} />
                    {/* Center play indicator */}
                    <Box sx={{
                        position: 'relative', zIndex: 1,
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Box sx={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '10px solid rgba(255,255,255,0.8)', ml: 0.3 }} />
                    </Box>
                    {/* LIVE badge */}
                    <Box sx={{
                        position: 'absolute', top: 5, left: 5,
                        bgcolor: '#EF4444', color: '#fff',
                        fontSize: '0.48rem', fontWeight: 800,
                        px: 0.6, py: 0.2, borderRadius: '3px', letterSpacing: 0.5,
                    }}>
                        ● LIVE
                    </Box>
                    {/* Stream type badge */}
                    <Box sx={{
                        position: 'absolute', bottom: 5, right: 5,
                        bgcolor: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.6)',
                        fontSize: '0.48rem', fontWeight: 600,
                        px: 0.6, py: 0.2, borderRadius: '3px',
                    }}>
                        {(plenary.streamType || 'hls').toUpperCase()}
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{
                        position: 'absolute', inset: 0, bgcolor: '#0D1B2A',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 0.5,
                    }}>
                        <Typography sx={{ fontSize: '1.2rem', opacity: 0.4 }}>📺</Typography>
                        <Typography sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.25)', letterSpacing: 1, textTransform: 'uppercase' }}>
                            No stream URL
                        </Typography>
                    </Box>
                </>
            )}

            {/* Viewer count simulation */}
            {hasStream && (plenary.viewerCount > 0) && (
                <Box sx={{
                    position: 'absolute', top: 5, left: plenary.streamUrl ? 38 : 5,
                    bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                    fontSize: '0.48rem', px: 0.6, py: 0.2, borderRadius: '3px',
                }}>
                    {plenary.viewerCount >= 1000
                        ? `${(plenary.viewerCount / 1000).toFixed(1)}k`
                        : plenary.viewerCount} Watching
                </Box>
            )}
        </Box>
    );
}

// ── Session zone content ──────────────────────────────────────────────────────
function SessionZoneContent({ plenary }) {
    return (
        <Box sx={{
            width: '100%', height: '100%',
            background: 'rgba(255,255,255,0.03)',
            display: 'flex', flexDirection: 'column',
            p: '8px 10px', gap: 0.75,
        }}>
            {/* LIVE badge simulation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ bgcolor: '#EF4444', color: '#fff', fontSize: '0.45rem', fontWeight: 800, px: 0.6, py: 0.2, borderRadius: '2px', letterSpacing: 0.5 }}>
                    ● LIVE
                </Box>
                <Box sx={{ height: 4, width: '30%', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
            </Box>

            {/* Title simulation */}
            <Box sx={{ height: 6, width: '75%', bgcolor: 'rgba(255,255,255,0.45)', borderRadius: 2 }} />
            <Box sx={{ height: 4, width: '55%', bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 2 }} />

            {/* Description lines */}
            <Box sx={{ height: 3, width: '90%', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, mt: 0.5 }} />
            <Box sx={{ height: 3, width: '80%', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2 }} />

            {/* Offline state hint if no sessions */}
            <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.5 }}>
                <EventNoteOutlined sx={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }} />
                <Typography sx={{ fontSize: '0.48rem', color: 'rgba(255,255,255,0.35)' }}>
                    {plenary.sessionIds?.length > 0
                        ? `${plenary.sessionIds.length} session ID(s) pinned`
                        : `Auto-match by location: "${plenary.name}"`}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Chat zone content ─────────────────────────────────────────────────────────
function ChatZoneContent({ plenary }) {
    return (
        <Box sx={{
            width: '100%', height: '100%', bgcolor: '#fff',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Chat header */}
            <Box sx={{
                px: 1, py: 0.75, borderBottom: '1px solid #f0f2f5',
                display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
            }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>
                    {plenary.chatTitle || 'Live Discussion'}
                </Typography>
                <Box sx={{ ml: 'auto', bgcolor: '#22C55E', color: '#fff', fontSize: '0.42rem', fontWeight: 700, px: 0.5, py: 0.15, borderRadius: '50px' }}>
                    ACTIVE
                </Box>
            </Box>

            {/* Simulated messages */}
            <Box sx={{ flex: 1, px: 1, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75, overflow: 'hidden' }}>
                {[
                    { w: '70%', color: '#1075BA' },
                    { w: '85%', color: '#8b4fcc' },
                    { w: '60%', color: '#1075BA' },
                    { w: '80%', color: '#22c55e' },
                ].map((m, i) => (
                    <Box key={i}>
                        <Box sx={{ height: 3, width: '30%', bgcolor: alpha(m.color, 0.5), borderRadius: 1, mb: 0.3 }} />
                        <Box sx={{ height: 3, width: m.w, bgcolor: '#f0f4f8', borderRadius: 1 }} />
                    </Box>
                ))}
            </Box>

            {/* Chat input */}
            <Box sx={{
                px: 1, py: 0.75, borderTop: '1px solid #f0f2f5',
                display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
            }}>
                <Box sx={{ flex: 1, height: 12, bgcolor: '#f8fafc', borderRadius: 10, border: '1px solid #e4e9f0' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: alpha('#1075BA', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Box sx={{ width: 4, height: 4, bgcolor: '#1075BA', borderRadius: '50%' }} />
                </Box>
            </Box>

            {/* Embed override notice */}
            {plenary.chatEmbedUrl && (
                <Box sx={{
                    position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.92)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 0.5,
                }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 14, color: '#22c55e' }} />
                    <Typography sx={{ fontSize: '0.5rem', color: '#22c55e', fontWeight: 700, textAlign: 'center', px: 1 }}>
                        External embed
                    </Typography>
                    <Typography sx={{ fontSize: '0.45rem', color: '#b0bac8', textAlign: 'center', px: 1, lineHeight: 1.4 }}>
                        {plenary.chatEmbedUrl.replace(/^https?:\/\//, '').slice(0, 28)}…
                    </Typography>
                </Box>
            )}
        </Box>
    );
}